import { useId, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { segnalaProblema } from "@/api/chat";
import { ultimiErrori } from "@/api/erroriRecenti";
import { etichettaPagina, praticaDalPercorso } from "./contestoPagina";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function SegnalaDialog({ open, onClose }: Props) {
  const { pathname } = useLocation();
  const campoId = useId();
  const [nota, setNota] = useState("");
  const [invio, setInvio] = useState(false);
  const [esito, setEsito] = useState<"ok" | "errore" | "vuota" | null>(null);

  if (!open) return null;

  const pagina = etichettaPagina(pathname);
  const pratica = praticaDalPercorso(pathname);

  async function invia() {
    if (!nota.trim()) {
      setEsito("vuota");
      return;
    }
    setInvio(true);
    setEsito(null);
    try {
      await segnalaProblema({
        nota: nota.trim(),
        pagina,
        percorso: pathname,
        praticaId: pratica,
        errori: ultimiErrori(),
      });
      setEsito("ok");
      setTimeout(() => {
        setNota("");
        setEsito(null);
        onClose();
      }, 2000);
    } catch {
      setEsito("errore");
    } finally {
      setInvio(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={invio ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${campoId}-titolo`}
        className="bg-card text-text rounded-xl border border-border shadow-xl w-full max-w-lg p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 id={`${campoId}-titolo`} className="text-base font-semibold">
            Segnala un problema
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="shrink-0 -m-1.5 p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-muted-foreground hover:bg-background/60 hover:text-text focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <X size={18} />
          </button>
        </div>

        <p className="text-xs text-muted-foreground mb-3">
          Da: {pagina}
          {pratica ? ` — pratica ${pratica}` : ""}
        </p>

        <label htmlFor={campoId} className="block text-sm font-medium mb-1.5">
          Che cosa è successo
        </label>
        <textarea
          id={campoId}
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          disabled={invio}
          rows={4}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm min-h-[104px] focus:outline-none focus:ring-2 focus:ring-brand disabled:opacity-50"
        />
        <p className="text-xs text-muted-foreground mt-1.5">
          Scrivi cosa stavi facendo e cosa non ha funzionato.
        </p>

        <div aria-live="polite" className="min-h-[1.25rem] mt-2">
          {esito === "vuota" && (
            <p className="text-xs text-red-600">Scrivi cosa non ha funzionato prima di inviare.</p>
          )}
          {esito === "ok" && (
            <p className="text-xs text-green-600">
              Segnalazione inviata. Ti avvisiamo quando è risolta.
            </p>
          )}
          {esito === "errore" && (
            <p className="text-xs text-red-600">Invio non riuscito. Riprova.</p>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={invio}
            className="px-4 min-h-[44px] text-sm border border-border rounded-lg hover:bg-background/60 focus:outline-none focus:ring-2 focus:ring-brand disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={invia}
            disabled={invio}
            className="px-4 min-h-[44px] text-sm font-medium bg-brand text-white rounded-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-brand disabled:opacity-50"
          >
            {invio ? "Invio…" : "Invia"}
          </button>
        </div>
      </div>
    </div>
  );
}
